import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, ChevronDown, ChevronUp, Loader, Pencil } from 'lucide-react';
import PromoCard from './PromoCard';
import BestBetCard from './BestBetCard';
import HedgePairCard from './HedgePairCard';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';

function getSportIcon(sportKey) {
  if (!sportKey) return '🏆';
  if (sportKey.includes('football')) return '🏈';
  if (sportKey.includes('basketball')) return '🏀';
  if (sportKey.includes('hockey')) return '🏒';
  if (sportKey.includes('soccer')) return '⚽';
  if (sportKey.includes('tennis')) return '🎾';
  if (sportKey.includes('mma')) return '🥊';
  if (sportKey.includes('baseball')) return '⚾';
  if (sportKey.includes('boxing')) return '🥊';
  return '🏆';
}

function shortSide(side) {
  if (!side) return '';
  if (/^(over|under)\s/i.test(side)) return side.split(' ')[0];
  const ouMatch = side.match(/\b(over|under)\s+([\d.]+)(\s*\([^)]+\))?/i);
  if (ouMatch) {
    const dir = ouMatch[1][0].toUpperCase() + ouMatch[1].slice(1).toLowerCase();
    const prop = ouMatch[3] ? ' ' + ouMatch[3].trim() : '';
    return `${dir} ${ouMatch[2]}${prop}`;
  }
  return side.split(' ').pop();
}

function oddsToDecimalLocal(val) {
  if (val === null || val === undefined) return 2.0;
  if (val > 0) return val / 100 + 1;
  return 100 / Math.abs(val) + 1;
}

function naturalRound(amount) {
  if (amount == null) return null;
  const increment = amount >= 1000 ? 10 : amount >= 100 ? 5 : 1;
  return Math.round(amount / increment) * increment;
}

function QualifyHedgeCard({ qr, promo, promoBookName, sportKey, sportTitle, session, onTrack, tracked, bestBonusProfit }) {
  const [trackingMode, setTrackingMode] = useState(false);
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');

  const decA = oddsToDecimalLocal(qr.qualify_odds);
  const decB = oddsToDecimalLocal(qr.qualify_hedge_odds);
  const pA = parseFloat(amountA) || 0;
  const pB = parseFloat(amountB) || 0;
  // Both outcomes guaranteed same cost (true hedge)
  const costIfAWins = pA * (decA - 1) - pB;
  const costIfBWins = pB * (decB - 1) - pA;

  function openTrack() {
    setAmountA(String(promo.amount));
    setAmountB(qr.qualify_hedge_amount != null ? String(naturalRound(qr.qualify_hedge_amount)) : '');
    setTrackingMode(true);
  }

  return (
    <div className="promo-card promo-medium">
      <div className="pc-header">
        <span className="pc-sport">{getSportIcon(sportKey)} {sportTitle || 'Sports'}</span>
        <span className="pc-market">Moneyline</span>
      </div>

      <div className="pc-event">{qr.qualify_event}</div>

      <div className="pc-hero pc-hero-medium">
        <div className="pc-hero-main">
          <span className="pc-hero-label">Qualifying cost</span>
          <span className="pc-hero-amount">−${qr.qualifying_cost?.toFixed(2)}</span>
        </div>
        <span className="pc-hero-secondary">unlocks ${promo.bonusAmount} bonus bet</span>
      </div>

      <div className="bag-strategy-note">
        {qr.qualifying_cost > 0.5
          ? `Hedge both sides for a fixed −$${qr.qualifying_cost.toFixed(2)} qualifying cost, which unlocks your $${promo.bonusAmount} bonus bet. The bonus converts into a much larger guaranteed win overall.`
          : qr.qualifying_cost <= 0
          ? `This qualifying bet hedges at no loss${qr.qualifying_cost < -0.01 ? ` — you actually pocket +$${Math.abs(qr.qualifying_cost).toFixed(2)} on it` : ''}, while unlocking your $${promo.bonusAmount} bonus bet.`
          : `This qualifying bet hedges at near-zero cost, unlocking your $${promo.bonusAmount} bonus bet which converts into guaranteed profit.`}
        {bestBonusProfit != null && (
          <span className="bag-strategy-net">
            Estimated profit after bonus:
            <strong>+${(bestBonusProfit - qr.qualifying_cost).toFixed(2)}</strong>
          </span>
        )}
      </div>

      {/* Step 1 — promo book */}
      <div className="pc-step">
        <span className="pc-step-num">1</span>
        <div className="pc-step-body">
          <div className="pc-book-row">
            <SportsbookLogo bookKey={promo.book} size={16} />
            <span className="pc-book-name">{promoBookName}</span>
          </div>
          <div className="pc-step-bet">
            <span className="pc-side">{qr.qualify_side}</span>
            <div className="pc-step-odds-amount">
              <span className="pc-odds">{qr.qualify_odds > 0 ? '+' : ''}{qr.qualify_odds}</span>
              <span className="pc-divider">·</span>
              <span className="pc-amount">${qr.qualify_amount}</span>
              <span className="pc-amount-note">real $</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 — hedge book */}
      <div className="pc-step">
        <span className="pc-step-num">2</span>
        <div className="pc-step-body">
          <div className="pc-book-row">
            <SportsbookLogo bookKey={qr.qualify_hedge_book} size={16} />
            <span className="pc-book-name">{qr.qualify_hedge_book_title}</span>
          </div>
          <div className="pc-step-bet">
            <span className="pc-side">{qr.qualify_hedge_side}</span>
            <div className="pc-step-odds-amount">
              <span className="pc-odds">{qr.qualify_hedge_odds > 0 ? '+' : ''}{qr.qualify_hedge_odds}</span>
              <span className="pc-divider">·</span>
              <span className="pc-amount">${qr.qualify_hedge_amount != null ? naturalRound(qr.qualify_hedge_amount) : '—'}</span>
              <span className="pc-amount-note">real $</span>
            </div>
          </div>
        </div>
      </div>

      {/* Outcome preview */}
      {pA > 0 && pB > 0 && (
        <div className="arb-profit-preview">
          <div className="arb-profit-preview-row">
            <span>{shortSide(qr.qualify_side)} wins:</span>
            <span className={costIfAWins >= 0 ? 'profit-pos' : 'profit-neg'}>
              {costIfAWins >= 0 ? '+' : ''}${costIfAWins.toFixed(2)}
            </span>
          </div>
          <div className="arb-profit-preview-row">
            <span>{shortSide(qr.qualify_hedge_side)} wins:</span>
            <span className={costIfBWins >= 0 ? 'profit-pos' : 'profit-neg'}>
              {costIfBWins >= 0 ? '+' : ''}${costIfBWins.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {session && !tracked && !trackingMode && (
        <button className="ev-track-btn" onClick={openTrack}>Track Bet</button>
      )}
      {session && trackingMode && (
        <div className="arb-track-form" onClick={e => e.stopPropagation()}>
          <div className="arb-track-row">
            <span className="arb-track-label">{promoBookName}</span>
            <input type="number" className="track-amount-input" value={amountA}
              onChange={e => setAmountA(e.target.value)} placeholder="Stake ($)" min="0" step="0.01" autoFocus />
          </div>
          <div className="arb-track-row">
            <span className="arb-track-label">{qr.qualify_hedge_book_title}</span>
            <input type="number" className="track-amount-input" value={amountB}
              onChange={e => setAmountB(e.target.value)} placeholder="Hedge stake ($)" min="0" step="0.01" />
          </div>
          <div className="track-amount-btns">
            <button className="track-amount-save" onClick={() => { onTrack(qr, amountA, amountB); setTrackingMode(false); }}>Track</button>
            <button className="track-amount-cancel" onClick={() => setTrackingMode(false)}>✕</button>
          </div>
        </div>
      )}
      {session && tracked && (
        <button className="ev-track-btn ev-track-btn--done" disabled>Tracked ✓</button>
      )}
    </div>
  );
}

function DepositRolloverCard({ opp, promo, bookName, session, rolledOver, totalRollover, onTrack }) {
  const [trackingMode, setTrackingMode] = useState(false);
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');

  const decA = oddsToDecimalLocal(opp.promo_odds);
  const decB = oddsToDecimalLocal(opp.hedge_odds);
  const pA = parseFloat(amountA) || 0;
  const pB = parseFloat(amountB) || 0;
  const costIfAWins = pA * (decA - 1) - pB;
  const costIfBWins = pB * (decB - 1) - pA;
  const isComplete = rolledOver >= totalRollover && totalRollover > 0;

  // Expected cost of all OTHER bets in the rollover (besides this one)
  const expectedOtherCosts = (opp.expected_cost || 0) * ((promo.playthrough || 1) - 1);
  // Static estimate before amounts are entered
  const estNetProfit = promo.amount - (opp.expected_cost || 0) * (promo.playthrough || 1);
  // Per-outcome net profit (bonus minus other bets' costs plus this bet's specific outcome)
  const netIfAWins = pA > 0 && pB > 0 ? promo.amount - expectedOtherCosts + costIfAWins : null;
  const netIfBWins = pA > 0 && pB > 0 ? promo.amount - expectedOtherCosts + costIfBWins : null;

  function openTrack() {
    setAmountA(String(opp.per_bet_amount || promo.amount));
    setAmountB(opp.hedge_amount != null ? String(naturalRound(opp.hedge_amount)) : '');
    setTrackingMode(true);
  }

  const marketLabel = opp.market === 'h2h' ? 'Moneyline'
    : opp.market === 'spreads' ? 'Spread'
    : opp.market === 'totals' ? 'Total'
    : opp.market || 'Moneyline';

  return (
    <div className="promo-card promo-medium">
      <div className="pc-header">
        <span className="pc-sport">{getSportIcon(opp.sport)} {opp.sport_title || 'Sports'}</span>
        <span className="pc-market">{marketLabel}</span>
      </div>

      <div className="pc-event">{opp.event}</div>

      <div className="pc-hero pc-hero-medium">
        <div className="pc-hero-main">
          <span className="pc-hero-label">Est. profit</span>
          <span className="pc-hero-amount" style={{color: '#10b981'}}>+${estNetProfit.toFixed(2)}</span>
        </div>
        <span className="pc-hero-secondary">after completing ${totalRollover.toFixed(0)} rollover</span>
      </div>

      {/* Step 1 — promo book */}
      <div className="pc-step">
        <span className="pc-step-num">1</span>
        <div className="pc-step-body">
          <div className="pc-book-row">
            <SportsbookLogo bookKey={promo.book} size={16} />
            <span className="pc-book-name">{bookName}</span>
          </div>
          <div className="pc-step-bet">
            <span className="pc-side">{opp.promo_side}</span>
            <div className="pc-step-odds-amount">
              <span className="pc-odds">{opp.promo_odds > 0 ? '+' : ''}{opp.promo_odds}</span>
              <span className="pc-divider">·</span>
              <span className="pc-amount">${naturalRound(opp.per_bet_amount || promo.amount)}</span>
              <span className="pc-amount-note">bonus $</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 — hedge book */}
      <div className="pc-step">
        <span className="pc-step-num">2</span>
        <div className="pc-step-body">
          <div className="pc-book-row">
            <SportsbookLogo bookKey={opp.hedge_book} size={16} />
            <span className="pc-book-name">{opp.hedge_book_title}</span>
          </div>
          <div className="pc-step-bet">
            <span className="pc-side">{opp.hedge_side}</span>
            <div className="pc-step-odds-amount">
              <span className="pc-odds">{opp.hedge_odds > 0 ? '+' : ''}{opp.hedge_odds}</span>
              <span className="pc-divider">·</span>
              <span className="pc-amount">${opp.hedge_amount != null ? naturalRound(opp.hedge_amount) : '—'}</span>
              <span className="pc-amount-note">real $</span>
            </div>
          </div>
        </div>
      </div>

      {/* Outcome preview — shows total net profit including bonus */}
      {netIfAWins != null && netIfBWins != null && (
        <div className="arb-profit-preview">
          <div className="arb-profit-preview-row">
            <span>{shortSide(opp.promo_side)} wins:</span>
            <span className={netIfAWins >= 0 ? 'profit-pos' : 'profit-neg'}>
              {netIfAWins >= 0 ? '+' : ''}${netIfAWins.toFixed(2)}
            </span>
          </div>
          <div className="arb-profit-preview-row">
            <span>{shortSide(opp.hedge_side)} wins:</span>
            <span className={netIfBWins >= 0 ? 'profit-pos' : 'profit-neg'}>
              {netIfBWins >= 0 ? '+' : ''}${netIfBWins.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {session && !isComplete && !trackingMode && (
        <button className="ev-track-btn" onClick={openTrack}>Track Rollover Bet</button>
      )}
      {session && trackingMode && (
        <div className="arb-track-form" onClick={e => e.stopPropagation()}>
          <div className="arb-track-row">
            <span className="arb-track-label">{bookName}</span>
            <input type="number" className="track-amount-input" value={amountA}
              onChange={e => setAmountA(e.target.value)} placeholder="Stake ($)" min="0" step="0.01" autoFocus />
          </div>
          <div className="arb-track-row">
            <span className="arb-track-label">{opp.hedge_book_title}</span>
            <input type="number" className="track-amount-input" value={amountB}
              onChange={e => setAmountB(e.target.value)} placeholder="Hedge stake ($)" min="0" step="0.01" />
          </div>
          <div className="track-amount-btns">
            <button className="track-amount-save" onClick={() => { onTrack(opp, amountA, amountB); setTrackingMode(false); }}>Track</button>
            <button className="track-amount-cancel" onClick={() => setTrackingMode(false)}>✕</button>
          </div>
        </div>
      )}
      {isComplete && (
        <button className="ev-track-btn ev-track-btn--done" disabled>Rollover complete ✓</button>
      )}
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const PROMO_TYPES = [
  { key: 'free_bet', label: 'Free Bet' },
  { key: 'no_sweat', label: 'No Sweat' },
  { key: 'profit_boost', label: 'Profit Boost' },
  { key: 'deposit_bonus', label: 'Deposit Bonus' },
  { key: 'bet_and_get', label: 'Bet & Get' },
];

const ALL_MARKET_KEYS = ['h2h', 'spreads', 'totals', 'props'];
const MARKET_OPTIONS = [
  { key: 'h2h', label: 'Moneyline' },
  { key: 'spreads', label: 'Spread' },
  { key: 'totals', label: 'Totals' },
  { key: 'props', label: 'Props' },
];

const STORAGE_KEY = 'savedPromos';

function loadPromos() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function savePromos(promos) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(promos));
}

function AddPromoModal({ onAdd, onSave, onClose, selectedBooks, editPromo }) {
  const editing = Boolean(editPromo);

  const [type, setType] = useState(editPromo?.type || 'free_bet');
  const [book, setBook] = useState(editPromo?.book || '');
  const [amount, setAmount] = useState(editPromo?.amount?.toString() || '');
  const [bonusAmount, setBonusAmount] = useState(editPromo?.bonusAmount?.toString() || '');
  const [boostPct, setBoostPct] = useState(editPromo?.boostPct?.toString() || '');
  const [playthrough, setPlaythrough] = useState(editPromo?.playthrough?.toString() || '1');
  const [sport, setSport] = useState(editPromo?.sport || 'all');
  const [liveStatus, setLiveStatus] = useState(editPromo?.liveStatus || 'all');
  const [minOdds, setMinOdds] = useState(editPromo?.minOdds?.toString() || '');
  const [expiresAt, setExpiresAt] = useState(editPromo?.expiresAt || '');
  const [gameDate, setGameDate] = useState(editPromo?.gameDate || '');
  const [markets, setMarkets] = useState(
    new Set((editPromo?.markets || ALL_MARKET_KEYS.join(',')).split(','))
  );

  const bookOptions = selectedBooks && selectedBooks.length > 0
    ? selectedBooks
    : Object.keys(SPORTSBOOK_NAMES);

  function toggleMarket(key) {
    const next = new Set(markets);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setMarkets(next);
  }

  function toggleAllMarkets() {
    if (markets.size === ALL_MARKET_KEYS.length) {
      setMarkets(new Set());
    } else {
      setMarkets(new Set(ALL_MARKET_KEYS));
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!book || !amount) return;
    if (type === 'bet_and_get' && !bonusAmount) return;
    const promo = {
      id: editing ? editPromo.id : Date.now().toString(),
      type,
      book,
      amount: parseFloat(amount),
      bonusAmount: type === 'bet_and_get' && bonusAmount ? parseFloat(bonusAmount) : null,
      boostPct: boostPct ? parseFloat(boostPct) : null,
      playthrough: playthrough ? parseFloat(playthrough) : 1,
      sport,
      liveStatus,
      markets: [...markets].join(','),
      minOdds: minOdds ? parseInt(minOdds, 10) : null,
      expiresAt: expiresAt || null,
      gameDate: gameDate || null,
      step: (type === 'bet_and_get' || type === 'no_sweat')
        ? (editing ? editPromo.step : 'qualifying')
        : undefined,
      qualifyingBetId: editing ? editPromo.qualifyingBetId : undefined,
      createdAt: editing ? editPromo.createdAt : new Date().toISOString(),
    };
    if (editing) {
      onSave(promo);
    } else {
      onAdd(promo);
    }
    onClose();
  }

  const typeLabel = PROMO_TYPES.find(t => t.key === type)?.label || type;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content promo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editing ? 'Edit Promotion' : 'Add Promotion'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="promo-form">
          {/* Type tabs — locked when editing */}
          {editing ? (
            <div className="form-group">
              <label>Promotion Type</label>
              <div className="promo-type-static">{typeLabel}</div>
            </div>
          ) : (
            <div className="promo-type-tabs">
              {PROMO_TYPES.map(t => (
                <button
                  key={t.key}
                  type="button"
                  className={`promo-type-tab ${type === t.key ? 'active' : ''}`}
                  onClick={() => setType(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Sportsbook */}
          <div className="form-group">
            <label>Sportsbook</label>
            <select value={book} onChange={e => setBook(e.target.value)} required>
              <option value="">Select sportsbook…</option>
              {bookOptions.map(k => (
                <option key={k} value={k}>{SPORTSBOOK_NAMES[k] || k}</option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div className="form-group">
            <label>
              {type === 'free_bet' ? 'Free Bet Amount ($)' :
               type === 'no_sweat' ? 'Bet Amount ($)' :
               type === 'profit_boost' ? 'Max Bet Amount ($)' :
               type === 'bet_and_get' ? 'Qualifying Bet Amount ($)' :
               'Bonus Amount ($)'}
            </label>
            <input
              type="number"
              min="1"
              step="0.01"
              placeholder="100"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              required
            />
          </div>

          {/* Bonus amount — bet_and_get only */}
          {type === 'bet_and_get' && (
            <div className="form-group">
              <label>Bonus Bet Amount ($)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="100"
                value={bonusAmount}
                onChange={e => setBonusAmount(e.target.value)}
                required
              />
              <span className="form-hint">The free bet you receive after qualifying</span>
            </div>
          )}

          {/* Boost % — profit_boost only */}
          {type === 'profit_boost' && (
            <div className="form-group">
              <label>Boost Percentage (%)</label>
              <input
                type="number"
                min="1"
                max="500"
                step="1"
                placeholder="25"
                value={boostPct}
                onChange={e => setBoostPct(e.target.value)}
                required
              />
            </div>
          )}

          {/* Playthrough — deposit_bonus only */}
          {type === 'deposit_bonus' && (
            <div className="form-group">
              <label>Playthrough Multiplier</label>
              <input
                type="number"
                min="1"
                max="20"
                step="0.5"
                placeholder="1"
                value={playthrough}
                onChange={e => setPlaythrough(e.target.value)}
                required
              />
              <span className="form-hint">
                e.g. 1 = must wager the bonus amount once
              </span>
            </div>
          )}

          {/* Sport filter */}
          <div className="form-group">
            <label>Sport (optional)</label>
            <select value={sport} onChange={e => setSport(e.target.value)}>
              <option value="all">All Sports</option>
              <option value="nba">NBA</option>
              <option value="wnba">WNBA</option>
              <option value="ncaab">NCAAB</option>
              <option value="ncaaw">NCAAW</option>
              <option value="euroleague">EuroLeague</option>
              <option value="nfl">NFL</option>
              <option value="ncaaf">NCAAF</option>
              <option value="mlb">MLB</option>
              <option value="nhl">NHL</option>
              <option value="soccer">Soccer</option>
              <option value="ufc">UFC/MMA</option>
              <option value="tennis">Tennis</option>
              <option value="boxing">Boxing</option>
            </select>
          </div>

          {/* Live / Upcoming filter */}
          <div className="form-group">
            <label>Game Status</label>
            <div className="promo-type-tabs">
              {[
                { key: 'all', label: 'All Games' },
                { key: 'live', label: 'Live Only' },
                { key: 'upcoming', label: 'Upcoming Only' },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  className={`promo-type-tab ${liveStatus === opt.key ? 'active' : ''}`}
                  onClick={() => setLiveStatus(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {liveStatus === 'live' && (
              <span className="form-hint">Only scans games currently in progress</span>
            )}
          </div>

          {/* Allowed Bet Types — checkboxes */}
          <div className="form-group">
            <label>Allowed Bet Types</label>
            <div className="market-checkboxes">
              <label className="market-check-row market-check-all">
                <input
                  type="checkbox"
                  checked={markets.size === ALL_MARKET_KEYS.length}
                  onChange={toggleAllMarkets}
                />
                <span>All</span>
              </label>
              {MARKET_OPTIONS.map(({ key, label }) => (
                <label key={key} className="market-check-row">
                  <input
                    type="checkbox"
                    checked={markets.has(key)}
                    onChange={() => toggleMarket(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Two-column row for min odds + expiry date */}
          <div className="promo-form-row">
            <div className="form-group">
              <label>Min Odds (optional)</label>
              <input
                type="number"
                step="1"
                placeholder="e.g. 300"
                value={minOdds}
                onChange={e => setMinOdds(e.target.value)}
              />
              <span className="form-hint">For "+300 or longer" promos</span>
            </div>

            <div className="form-group">
              <label>Promo Expires (optional)</label>
              <input
                type="date"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
              />
              <span className="form-hint">When the promo must be used by</span>
            </div>
          </div>

          {/* Game date filter */}
          <div className="promo-form-row">
            <div className="form-group">
              <label>Show Games Through (optional)</label>
              <input
                type="date"
                value={gameDate}
                onChange={e => setGameDate(e.target.value)}
              />
              <span className="form-hint">Only show games on or before this date</span>
            </div>
          </div>

          <button type="submit" className="promo-submit-btn">
            {editing ? 'Save Changes' : 'Find Best Options'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Default scale: -300 to -110 (step 10), Even, +110 to +300 (step 10)
// null = "Even" money, exact center (index 20 of 40)
const DEFAULT_ODDS_SCALE = [
  ...Array.from({ length: 20 }, (_, i) => -300 + i * 10),
  null,
  ...Array.from({ length: 20 }, (_, i) => 110 + i * 10),
];
const DEFAULT_ODDS_IDX = 25; // +150

// Build a scale appropriate for the promo's minOdds constraint.
// If minOdds is set (e.g. +200), returns an underdog-only scale from minOdds to +1500, step 25.
// Rightmost = +1500 = show all; leftmost = minOdds = most restrictive.
function buildOddsScale(minOdds) {
  if (!minOdds || minOdds <= 0) return DEFAULT_ODDS_SCALE;
  const start = Math.ceil(minOdds / 25) * 25; // snap to nearest 25
  const end = 1500;
  const count = Math.floor((end - start) / 25) + 1;
  return Array.from({ length: count }, (_, i) => start + i * 25);
}

// Initial slider index: rightmost (show all) when minOdds is set, else +150.
function buildInitialOddsIdx(minOdds, scale) {
  if (!minOdds || minOdds <= 0) return DEFAULT_ODDS_IDX;
  return scale.length - 1;
}

function oddsLabel(val) {
  if (val === null) return 'Even';
  return val > 0 ? `+${val}` : `${val}`;
}

function oddsToDecimal(val) {
  if (val === null) return 2.0;
  if (val > 0) return val / 100 + 1;
  return 100 / Math.abs(val) + 1;
}

function PromoSection({ promo, selectedBooks, onRemove, onUpdate, onEdit, bankroll, kellyFraction, session }) {
  const [results, setResults] = useState([]);
  const [bestBets, setBestBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedLeft, setExpandedLeft] = useState(false);
  const [expandedRight, setExpandedRight] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const [maxHedge, setMaxHedge] = useState('');
  const [liveFilter, setLiveFilter] = useState(promo.liveStatus === 'live' ? 'live' : 'all');
  const [mobileTab, setMobileTab] = useState('lock');
  const oddsScale = buildOddsScale(promo.minOdds);
  const [oddsIdx, setOddsIdx] = useState(() => buildInitialOddsIdx(promo.minOdds, buildOddsScale(promo.minOdds)));

  // Bet & Get qualifying step state
  const [qualifyLiveFilter, setQualifyLiveFilter] = useState(promo.liveStatus === 'live' ? 'live' : 'all');
  const [qualifyTrackMode, setQualifyTrackMode] = useState(null);
  const [qualifyAmount, setQualifyAmount] = useState('');
  const [qualifyTracked, setQualifyTracked] = useState(false);
  const [qualifyExpanded, setQualifyExpanded] = useState(false);
  const [qualifyBBExpanded, setQualifyBBExpanded] = useState(false);
  const [showManualFix, setShowManualFix] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // qualifying_bet_id stored in localStorage (server strips unknown fields from PUT)
  const BAG_QID_KEY = `bag_qid_${promo.id}`;

  // Deposit bonus rollover progress stored in localStorage
  const DEPOSIT_ROLLED_KEY = `deposit_rolled_${promo.id}`;
  const totalRollover = promo.type === 'deposit_bonus'
    ? (promo.amount || 0) * (promo.playthrough || 1)
    : 0;
  const [depositRolledOver, setDepositRolledOver] = useState(() => {
    const v = localStorage.getItem(DEPOSIT_ROLLED_KEY);
    return v ? parseFloat(v) : 0;
  });
  const isRolloverComplete = totalRollover > 0 && depositRolledOver >= totalRollover;
  const qualifyingBetId = (() => {
    const v = localStorage.getItem(BAG_QID_KEY);
    return v ? parseInt(v, 10) : undefined;
  })();

  useEffect(() => {
    const scale = buildOddsScale(promo.minOdds);
    setOddsIdx(buildInitialOddsIdx(promo.minOdds, scale));
  }, [promo.minOdds]);

  const PREVIEW_COUNT = 3;
  const BEST_BET_MAX = 10;

  function advanceStep(extraFields) {
    let next;
    if (promo.type === 'no_sweat') {
      next = promo.step === 'qualifying' ? 'awaiting_result' : 'free_bet';
    } else {
      next = promo.step === 'qualifying' ? 'awaiting_bonus' : 'bonus';
    }
    onUpdate({ ...promo, step: next, ...(extraFields || {}) });
  }

  function parseCommenceTime(ct) {
    if (!ct) return null;
    return /Z|[+-]\d{2}:\d{2}$/.test(ct) ? new Date(ct) : new Date(ct + 'Z');
  }

  function getQualifyOptions() {
    const opts = results.length > 0 ? (results[0].qualify_options || (results[0].qualify_event ? [results[0]] : [])) : [];
    const now2 = Date.now();
    const filtered = opts.filter(o => {
      const ts = parseCommenceTime(o.qualify_commence_time);
      if (qualifyLiveFilter === 'live') return ts && ts.getTime() <= now2;
      if (qualifyLiveFilter === 'upcoming') return !ts || ts.getTime() > now2;
      return true;
    });
    return qualifyLiveFilter === 'all' && filtered.length === 0 ? opts : filtered;
  }

  async function handleTrackQualifying(mode, qr, bb, amtA, amtB) {
    const isHedge = mode === 'hedge';
    const body = {
      betType: 'promo',
      sportsbook: promo.book,
      eventName: isHedge ? qr?.qualify_event : bb?.event,
      sportKey: isHedge ? (results[0]?.sport || null) : bb?.sport,
      market: isHedge ? 'h2h' : (bb?.market || 'h2h'),
      side: isHedge ? qr?.qualify_side : bb?.promo_side,
      odds: isHedge ? qr?.qualify_odds : bb?.promo_odds,
      amount: parseFloat(amtA) || promo.amount,
      notes: JSON.stringify({
        event_id: isHedge ? qr?.qualify_event_id : bb?.event_id,
        sport_key: isHedge ? (results[0]?.sport || null) : bb?.sport,
        home_team: isHedge ? null : bb?.home_team,
        away_team: isHedge ? null : bb?.away_team,
        side: isHedge ? qr?.qualify_side : bb?.promo_side,
        point: isHedge ? null : (bb?.point ?? null),
        commence_time: isHedge ? qr?.qualify_commence_time : bb?.commence_time,
        promo_type: 'bet_and_get',
        stage: 'qualifying',
      }),
    };

    if (isHedge && qr?.qualify_hedge_side) {
      body.sportsbookB = qr.qualify_hedge_book;
      body.sideB = qr.qualify_hedge_side;
      body.oddsB = qr.qualify_hedge_odds;
      body.amountB = parseFloat(amtB) || qr.qualify_hedge_amount;
    }

    try {
      const res = await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem(BAG_QID_KEY, String(data.bet.id));
        setQualifyTracked(true);
        setQualifyTrackMode(null);
        advanceStep({ qualifyingBetId: data.bet.id });
      }
    } catch (err) {
      console.error('Track qualifying failed:', err);
    }
  }

  async function handleTrackRollover(opp, amtA, amtB) {
    const stakeA = parseFloat(amtA) || opp.per_bet_amount || promo.amount;
    const body = {
      betType: 'promo',
      sportsbook: promo.book,
      sportsbookB: opp.hedge_book,
      eventName: opp.event,
      sportKey: opp.sport,
      market: opp.market || 'h2h',
      side: opp.promo_side,
      odds: opp.promo_odds,
      amount: stakeA,
      sideB: opp.hedge_side,
      oddsB: opp.hedge_odds,
      amountB: parseFloat(amtB) || opp.hedge_amount,
      notes: JSON.stringify({
        stage: 'deposit_rollover',
        promo_id: String(promo.id),
        promo_type: 'deposit_bonus',
        promo_book: promo.book,
        bonus_amount: promo.amount,
        total_rollover: totalRollover,
        event_id: opp.event_id,
        commence_time: opp.commence_time,
      }),
    };
    try {
      const res = await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        const newRolled = depositRolledOver + stakeA;
        setDepositRolledOver(newRolled);
        localStorage.setItem(DEPOSIT_ROLLED_KEY, String(newRolled));
      }
    } catch (err) {
      console.error('Track rollover failed:', err);
    }
  }

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const effectiveType = promo.type === 'no_sweat' && promo.step === 'free_bet' ? 'free_bet' : promo.type;
      const params = new URLSearchParams({
        type: effectiveType,
        book: promo.book,
        amount: promo.amount,
        max_results: 200,
      });
      if (promo.boostPct) params.set('boost_pct', promo.boostPct);
      if (promo.playthrough) params.set('playthrough', promo.playthrough);
      if (promo.bonusAmount) params.set('bonus_amount', promo.bonusAmount);
      if (promo.sport && promo.sport !== 'all') params.set('sport', promo.sport);
      if (promo.liveStatus && promo.liveStatus !== 'all') params.set('live_status', promo.liveStatus);
      if (promo.minOdds) params.set('min_odds', promo.minOdds);
      if (promo.gameDate) params.set('game_date', promo.gameDate);
      params.set('markets', promo.markets || ALL_MARKET_KEYS.join(','));
      if (selectedBooks && selectedBooks.length > 0) {
        params.set('books', selectedBooks.join(','));
      }

      const res = await fetch(`${API_BASE}/promos/calculate?${params}`);
      const data = await res.json();
      if (data.success) {
        setResults(data.opportunities || []);
        setBestBets(data.best_bets || []);
        setScanInfo(data.scan_info || null);
      } else {
        setError(data.error || 'Failed to fetch opportunities');
      }
    } catch (err) {
      setError('Network error — is the backend running?');
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [promo, selectedBooks]);

  useEffect(() => {
    fetchResults();
    const interval = setInterval(fetchResults, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchResults]);

  const typeLabel = PROMO_TYPES.find(t => t.key === promo.type)?.label || promo.type;
  const bookName = SPORTSBOOK_NAMES[promo.book] || promo.book;
  const promoDesc = promo.type === 'free_bet'
    ? `$${promo.amount} free bet`
    : promo.type === 'no_sweat'
    ? `$${promo.amount} no-sweat bet`
    : promo.type === 'profit_boost'
    ? `${promo.boostPct}% boost up to $${promo.amount}`
    : promo.type === 'bet_and_get'
    ? `Bet $${promo.amount}, get $${promo.bonusAmount} bonus`
    : `$${promo.amount} bonus (${promo.playthrough}x playthrough)`;

  const showExpiry = promo.expiresAt && (promo.type !== 'bet_and_get' || !promo.step || promo.step === 'qualifying');
  const expiryLabel = showExpiry
    ? `Expires ${new Date(promo.expiresAt + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : null;
  const gameDateLabel = promo.gameDate
    ? `Games through ${new Date(promo.gameDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : null;
  const minOddsLabel = promo.minOdds ? `${oddsLabel(promo.minOdds)} or longer` : null;

  const showBestBets = !['deposit_bonus'].includes(promo.type);

  // Strict filter: only show bets within the slider's max odds range
  const now = Date.now();
  const maxDec = oddsToDecimal(oddsScale[oddsIdx]);
  const displayBets = bestBets.filter(bb => {
    if (oddsToDecimal(bb.promo_odds) > maxDec) return false;
    if (liveFilter === 'live') return bb.commence_time && new Date(bb.commence_time).getTime() <= now;
    if (liveFilter === 'upcoming') return !bb.commence_time || new Date(bb.commence_time).getTime() > now;
    return true;
  });

  // Filter lock-in results by max hedge budget and live status
  const maxHedgeNum = maxHedge !== '' ? parseFloat(maxHedge) : null;
  const displayResults = results.filter(r => {
    if (maxHedgeNum > 0 && (r.hedge_amount ?? 0) > maxHedgeNum) return false;
    if (liveFilter === 'live') return r.commence_time && new Date(r.commence_time).getTime() <= now;
    if (liveFilter === 'upcoming') return !r.commence_time || new Date(r.commence_time).getTime() > now;
    return true;
  });

  // Best stat for panel headers
  const bestGuaranteed = displayResults.length > 0
    ? Math.max(...displayResults.map(r => r.guaranteed_profit ?? r.net_profit ?? r.net_bonus_value ?? 0))
    : null;
  const bestEdge = displayBets.length > 0
    ? Math.max(...displayBets.map(b => b.edge_pct))
    : null;

  return (
    <div className="promo-section">
      <div className="promo-section-header">
        <div className="promo-section-info">
          <span className={`promo-type-badge promo-type-${promo.type}`}>{typeLabel}</span>
          <span className="promo-section-book">{bookName}</span>
          <span className="promo-section-desc">{promoDesc}</span>
          {minOddsLabel && <span className="promo-section-tag">{minOddsLabel}</span>}
          {promo.liveStatus === 'live' && <span className="promo-section-tag promo-tag-live">Live only</span>}
          {promo.liveStatus === 'upcoming' && <span className="promo-section-tag">Upcoming only</span>}
          {expiryLabel && <span className="promo-section-tag promo-expiry">{expiryLabel}</span>}
          {gameDateLabel && <span className="promo-section-tag">{gameDateLabel}</span>}
        </div>
        <button
          className="promo-collapse-btn"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
        </button>
        <button
          className="promo-edit-btn"
          onClick={() => onEdit(promo)}
          title="Edit promo"
        >
          <Pencil size={14} />
        </button>
        <button
          className="promo-remove-btn"
          onClick={() => onRemove(promo.id)}
          title="Remove promo"
        >
          <X size={16} />
        </button>
      </div>

      {!collapsed && loading && (
        <div className="promo-loading">
          <Loader size={18} className="spin" />
          <span>Finding best opportunities…</span>
        </div>
      )}

      {!collapsed && error && (
        <div className="promo-error">{error}</div>
      )}

      {!collapsed && fetched && !loading && !error && results.length === 0 && bestBets.length === 0 && (
        <div className="promo-empty">
          {scanInfo && scanInfo.games > 0 && scanInfo.prop_groups === 0 && promo.markets && promo.markets.includes('props') && !promo.markets.replace('props','').replace(/,/g,'').trim()
            ? `Player prop data is still loading — results will appear on the next refresh.`
            : scanInfo && scanInfo.games > 0
            ? `${bookName} doesn't have odds on the ${scanInfo.games} active game${scanInfo.games !== 1 ? 's' : ''} right now — they may not cover this sport or have suspended live lines.`
            : scanInfo && scanInfo.games === 0
            ? `No active games found for this sport right now.`
            : `No opportunities found right now.`}
        </div>
      )}

      {/* Bet & Get step tracker */}
      {!collapsed && promo.type === 'bet_and_get' && fetched && (results.length > 0 || bestBets.length > 0) && (
        <>
          {promo.step === 'qualifying' && (() => {
            const qualifyOptions = getQualifyOptions();
            const bestCost = qualifyOptions.length > 0 ? qualifyOptions[0].qualifying_cost : null;
            return (
              <>
                {/* Step header */}
                <div className="bag-qualify-header">
                  <span className="bag-step-chip">Step 1 of 2</span>
                  <span className="bag-qualify-header-desc">
                    Place a ${promo.amount} qualifying bet at {bookName} to unlock your ${promo.bonusAmount} bonus.
                  </span>
                </div>

                <div className={`promo-results-split${showBestBets ? '' : ' promo-results-single'} mobile-tab-${mobileTab}`}>
                  <div className="promo-mobile-tabs">
                    <button className={`pmt-btn${mobileTab === 'lock' ? ' active' : ''}`} onClick={() => setMobileTab('lock')}>🔒 Lock It In</button>
                    <button className={`pmt-btn${mobileTab === 'best' ? ' active' : ''}`} onClick={() => setMobileTab('best')}>📈 Best Bet</button>
                  </div>

                  {/* Lock It In panel */}
                  <div className="promo-panel promo-panel--lock">
                    <div className="promo-panel-header">
                      <span className="promo-panel-title">🔒 Lock It In</span>
                      {bestCost !== null && <span className="promo-panel-stat">−${bestCost.toFixed(2)} best cost</span>}
                    </div>

                    <div className="promo-live-filter">
                      {['all', 'live', 'upcoming'].map(opt => {
                        const locked = promo.liveStatus === 'live' && opt === 'upcoming';
                        return (
                          <button
                            key={opt}
                            className={`plf-btn${qualifyLiveFilter === opt ? ' active' : ''}${locked ? ' locked' : ''}`}
                            onClick={() => { if (!locked) { setQualifyLiveFilter(opt); setQualifyTrackMode(null); } }}
                            disabled={locked}
                          >
                            {opt === 'all' ? 'All' : opt === 'live' ? 'Live' : 'Upcoming'}
                          </button>
                        );
                      })}
                    </div>

                    {qualifyOptions.length > 0 ? (() => {
                      const QUALIFY_PREVIEW = 3;
                      const QUALIFY_MAX = 8;
                      const shown = qualifyExpanded ? qualifyOptions.slice(0, QUALIFY_MAX) : qualifyOptions.slice(0, QUALIFY_PREVIEW);
                      const sportKey2 = results.length > 0 ? results[0].sport : null;
                      const sportTitle2 = results.length > 0 ? results[0].sport_title : null;
                      const bestBonusProfit2 = results.length > 0 ? results[0].guaranteed_profit : null;
                      return (
                        <>
                          {shown.map((qr, qi) => (
                            <QualifyHedgeCard
                              key={`${qr.qualify_event_id}-${qr.qualify_side}-${qi}`}
                              qr={qr}
                              promo={promo}
                              promoBookName={bookName}
                              sportKey={sportKey2}
                              sportTitle={sportTitle2}
                              bestBonusProfit={bestBonusProfit2}
                              session={session}
                              tracked={qualifyTracked}
                              onTrack={(qr2, amtA, amtB) => handleTrackQualifying('hedge', qr2, null, amtA, amtB)}
                            />
                          ))}
                          {qualifyOptions.length > QUALIFY_PREVIEW && (
                            <button className="promo-expand-btn" onClick={() => setQualifyExpanded(e => !e)}>
                              {qualifyExpanded
                                ? <><ChevronUp size={14} /> Show less</>
                                : <><ChevronDown size={14} /> Show {Math.min(qualifyOptions.length - QUALIFY_PREVIEW, 5)} more options</>}
                            </button>
                          )}
                        </>
                      );
                    })() : (
                      <div className="promo-panel-empty">No {qualifyLiveFilter} qualifying games right now.</div>
                    )}
                    {!session && qualifyOptions.length > 0 && (
                      <button className="bag-advance-btn" style={{marginTop: 8}} onClick={() => advanceStep()}>
                        I've placed this bet →
                      </button>
                    )}
                  </div>

                  {/* Best Bet panel */}
                  {showBestBets && (
                    <div className="promo-panel promo-panel-ev promo-panel--best">
                      <div className="promo-panel-header">
                        <span className="promo-panel-title">📈 Best Bet</span>
                        {bestEdge !== null && (
                          <span className="promo-panel-stat promo-panel-stat-ev">
                            {bestEdge > 0 ? '+' : ''}{bestEdge.toFixed(1)}% best edge
                          </span>
                        )}
                      </div>
                      <div className="bbc-odds-slider">
                        <div className="bbc-slider-label">
                          <span>Max odds</span>
                          <span className="bbc-slider-value">{oddsLabel(oddsScale[oddsIdx])}</span>
                        </div>
                        <input type="range" className="bbc-slider" min={0} max={oddsScale.length - 1} step={1}
                          value={oddsIdx} onChange={e => setOddsIdx(parseInt(e.target.value))} />
                        <div className="bbc-slider-ticks">
                          <span>{oddsLabel(oddsScale[0])}</span>
                          <span>{oddsLabel(oddsScale[Math.floor((oddsScale.length - 1) / 2)])}</span>
                          <span>{oddsLabel(oddsScale[oddsScale.length - 1])}</span>
                        </div>
                      </div>

                      {displayBets.length > 0 ? (
                        <>
                          {(qualifyBBExpanded ? displayBets.slice(0, BEST_BET_MAX) : displayBets.slice(0, PREVIEW_COUNT)).map((bb, i) => {
                            const trackKey = `bb-${i}`;
                            return (
                              <div key={`${bb.event_id}-${bb.promo_side}-${i}`} className="bag-bestbet-qualify-wrap">
                                <BestBetCard opportunity={bb} promo={promo} bankroll={bankroll} kellyFraction={kellyFraction} session={null} />
                                {session && !qualifyTracked && qualifyTrackMode !== trackKey && (
                                  <button className="ev-track-btn bag-track-qualifying-btn"
                                    onClick={() => { setQualifyAmount(String(promo.amount)); setQualifyTrackMode(trackKey); }}>
                                    Track as Qualifying Bet
                                  </button>
                                )}
                                {session && qualifyTrackMode === trackKey && (
                                  <div className="track-amount-form" onClick={e => e.stopPropagation()}>
                                    <input type="number" className="track-amount-input" value={qualifyAmount}
                                      onChange={e => setQualifyAmount(e.target.value)} placeholder="Stake ($)" min="0" step="1" autoFocus />
                                    <button className="track-amount-save" onClick={() => handleTrackQualifying('bestbet', null, bb, qualifyAmount, null)}>Save</button>
                                    <button className="track-amount-cancel" onClick={() => setQualifyTrackMode(null)}>✕</button>
                                  </div>
                                )}
                                {qualifyTracked && <button className="ev-track-btn ev-track-btn--done" disabled>Tracked ✓</button>}
                              </div>
                            );
                          })}
                          {displayBets.length > PREVIEW_COUNT && (
                            <button className="promo-expand-btn" onClick={() => setQualifyBBExpanded(e => !e)}>
                              {qualifyBBExpanded
                                ? <><ChevronUp size={14} /> Show less</>
                                : <><ChevronDown size={14} /> Show {Math.min(displayBets.length - PREVIEW_COUNT, BEST_BET_MAX - PREVIEW_COUNT)} more options</>}
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="promo-panel-empty">No best bets found at {bookName}.</div>
                      )}

                      {!session && displayBets.length > 0 && (
                        <button className="bag-advance-btn" style={{marginTop: 8}} onClick={() => advanceStep()}>
                          I've placed this bet →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {promo.step === 'awaiting_bonus' && (
            <div className="bet-and-get-step bag-waiting">
              <div className="bag-step-label">Step 2 of 2 — Waiting for Your Bonus</div>
              <p className="bag-step-desc">
                Your qualifying bet is placed. Bonus bets typically arrive within 24–48 hours of the qualifying bet settling.
                Come back here once your ${promo.bonusAmount} bonus bet shows up in your account.
              </p>
              <button className="bag-advance-btn" onClick={() => advanceStep()}>
                My bonus arrived →
              </button>
            </div>
          )}

          {promo.step === 'bonus' && (
            <div className="bet-and-get-step">
              <div className="bag-step-label">Step 2 of 2 — Convert Your ${promo.bonusAmount} Bonus Bet</div>
              {results.length > 0 && (
                <p className="bag-step-desc">
                  Qualifying cost was −${results[0]?.qualifying_cost?.toFixed(2)}.
                  Best net guaranteed profit: +${results[0]?.net_profit?.toFixed(2)}.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Deposit bonus rollover section */}
      {!collapsed && promo.type === 'deposit_bonus' && fetched && !loading && !error && (() => {
        const progressPct = totalRollover > 0 ? Math.min(100, (depositRolledOver / totalRollover) * 100) : 0;
        const bestCost = displayResults.length > 0
          ? Math.min(...displayResults.map(r => r.expected_cost ?? Infinity))
          : null;
        return (
          <>
            <div className="deposit-bonus-explainer">
              <strong>{bookName}</strong> is matching your deposit with ${promo.amount} in bonus funds, but you can't withdraw it directly — you need to wager ${totalRollover.toFixed(2)} first ({promo.playthrough}× rollover). This tool finds the cheapest hedged bets to meet that requirement so you keep the maximum amount of your bonus guaranteed.
            </div>

            {depositRolledOver > 0 && (
              <div className="deposit-progress">
                <div className="deposit-progress-header">
                  <span>Rolled over: <strong>${depositRolledOver.toFixed(2)}</strong> of ${totalRollover.toFixed(2)}</span>
                  <span className="deposit-progress-pct">{progressPct.toFixed(0)}%</span>
                </div>
                <div className="deposit-progress-bar">
                  <div className="deposit-progress-fill" style={{width: `${progressPct}%`}} />
                </div>
                {isRolloverComplete && (
                  <div className="deposit-complete-msg">
                    Rollover complete! You can now withdraw your bonus.
                  </div>
                )}
              </div>
            )}

            {!isRolloverComplete && (results.length > 0 || bestBets.length > 0 ? (
              <div className={`promo-results-split mobile-tab-${mobileTab}`}>
                <div className="promo-mobile-tabs">
                  <button className={`pmt-btn${mobileTab === 'lock' ? ' active' : ''}`} onClick={() => setMobileTab('lock')}>🔒 Lock It In</button>
                  <button className={`pmt-btn${mobileTab === 'best' ? ' active' : ''}`} onClick={() => setMobileTab('best')}>📈 Best Bet</button>
                </div>
                {/* Lock It In panel */}
                <div className="promo-panel promo-panel--lock">
                  <div className="promo-panel-header">
                    <span className="promo-panel-title">🔒 Lock It In</span>
                    {bestCost !== null && (
                      <span className="promo-panel-stat" style={{color: '#10b981'}}>
                        +${(promo.amount - bestCost * (promo.playthrough || 1)).toFixed(2)} est. profit
                      </span>
                    )}
                  </div>
                  <div className="promo-live-filter">
                    {['all', 'live', 'upcoming'].map(opt => {
                      const locked = promo.liveStatus === 'live' && opt === 'upcoming';
                      return (
                        <button key={opt}
                          className={`plf-btn${liveFilter === opt ? ' active' : ''}${locked ? ' locked' : ''}`}
                          onClick={() => { if (!locked) { setLiveFilter(opt); setExpandedLeft(false); } }}
                          disabled={locked}>
                          {opt === 'all' ? 'All' : opt === 'live' ? 'Live' : 'Upcoming'}
                        </button>
                      );
                    })}
                  </div>
                  {displayResults.length > 0 ? (
                    <>
                      {(expandedLeft ? displayResults.slice(0, BEST_BET_MAX) : displayResults.slice(0, PREVIEW_COUNT)).map((opp, i) => (
                        <DepositRolloverCard
                          key={`${opp.event_id}-${opp.promo_side}-${i}`}
                          opp={opp}
                          promo={promo}
                          bookName={bookName}
                          session={session}
                          rolledOver={depositRolledOver}
                          totalRollover={totalRollover}
                          onTrack={handleTrackRollover}
                        />
                      ))}
                      {displayResults.length > PREVIEW_COUNT && (
                        <button className="promo-expand-btn" onClick={() => setExpandedLeft(!expandedLeft)}>
                          {expandedLeft
                            ? <><ChevronUp size={14} /> Show less</>
                            : <><ChevronDown size={14} /> View all {Math.min(displayResults.length, BEST_BET_MAX)} options</>}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="promo-panel-empty">
                      {liveFilter !== 'all' ? `No ${liveFilter} opportunities right now.` : 'No hedging opportunities found.'}
                    </div>
                  )}
                </div>

                {/* Best Bet panel */}
                <div className="promo-panel promo-panel-ev promo-panel--best">
                  <div className="promo-panel-header">
                    <span className="promo-panel-title">📈 Best Bet</span>
                    {bestEdge !== null && (
                      <span className="promo-panel-stat promo-panel-stat-ev">
                        {bestEdge > 0 ? '+' : ''}{bestEdge.toFixed(1)}% best edge
                      </span>
                    )}
                  </div>
                  <div className="bbc-odds-slider">
                    <div className="bbc-slider-label">
                      <span>Max odds</span>
                      <span className="bbc-slider-value">{oddsLabel(oddsScale[oddsIdx])}</span>
                    </div>
                    <input type="range" className="bbc-slider" min={0} max={oddsScale.length - 1} step={1}
                      value={oddsIdx} onChange={e => { setOddsIdx(parseInt(e.target.value)); setExpandedRight(false); }} />
                    <div className="bbc-slider-ticks">
                      <span>{oddsLabel(oddsScale[0])}</span>
                      <span>{oddsLabel(oddsScale[Math.floor((oddsScale.length - 1) / 2)])}</span>
                      <span>{oddsLabel(oddsScale[oddsScale.length - 1])}</span>
                    </div>
                  </div>
                  {displayBets.length > 0 ? (
                    <>
                      <div className="promo-results">
                        {(expandedRight ? displayBets.slice(0, BEST_BET_MAX) : displayBets.slice(0, PREVIEW_COUNT)).map((bb, i) => (
                          <BestBetCard key={`${bb.event_id}-${bb.promo_side}-${i}`} opportunity={bb} promo={promo} bankroll={bankroll} kellyFraction={kellyFraction} session={session} />
                        ))}
                      </div>
                      {displayBets.length > PREVIEW_COUNT && (
                        <button className="promo-expand-btn" onClick={() => setExpandedRight(!expandedRight)}>
                          {expandedRight
                            ? <><ChevronUp size={14} /> Show less</>
                            : <><ChevronDown size={14} /> View all {Math.min(displayBets.length, BEST_BET_MAX)} options</>}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="promo-panel-empty">No best-bet plays found.</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="promo-empty">No opportunities found right now.</div>
            ))}
          </>
        );
      })()}

      {/* No-Sweat step tracker */}
      {!collapsed && promo.type === 'no_sweat' && promo.step && fetched && (
        <>
          {promo.step === 'qualifying' && (results.length > 0 || bestBets.length > 0) && (
            <div className="bag-qualify-header">
              <span className="bag-step-chip">Step 1 of 2</span>
              <span className="bag-qualify-header-desc">
                Place your ${promo.amount} no-sweat bet. If it loses, {bookName} will refund your stake as a free bet.
              </span>
            </div>
          )}
          {promo.step === 'awaiting_result' && (
            <div className="bet-and-get-step bag-waiting">
              <div className="bag-step-label">Waiting for Your Bet to Settle</div>
              {session ? (
                <>
                  <p className="bag-step-desc">
                    Your ${promo.amount} no-sweat bet is tracked. We'll automatically detect the result — if it wins you're done, if it loses your free bet will appear in Step 2.
                  </p>
                  {!showManualFix ? (
                    <button className="ns-fix-btn" onClick={() => setShowManualFix(true)}>
                      Wrong result? Fix manually →
                    </button>
                  ) : (
                    <div className="ns-result-btns">
                      <button className="ns-result-btn ns-result-btn--win" onClick={() => { onUpdate({ ...promo, step: 'done' }); setShowManualFix(false); }}>
                        ✓ Bet Won — No More Steps
                      </button>
                      <button className="ns-result-btn ns-result-btn--loss" onClick={() => { advanceStep(); setShowManualFix(false); }}>
                        Bet Lost — Use My Free Bet →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="bag-step-desc">
                    Your ${promo.amount} no-sweat bet is placed. Wait for the result — if it wins you're done, if it loses {bookName} will refund your ${promo.amount} stake as a free bet.
                  </p>
                  <div className="ns-result-btns">
                    <button className="ns-result-btn ns-result-btn--win" onClick={() => onUpdate({ ...promo, step: 'done' })}>
                      ✓ Bet Won — No More Steps
                    </button>
                    <button className="ns-result-btn ns-result-btn--loss" onClick={() => advanceStep()}>
                      Bet Lost — Use My Free Bet →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {promo.step === 'done' && (
            <div className="bet-and-get-step">
              <div className="bag-step-label" style={{color: '#10b981'}}>✓ Complete</div>
              <p className="bag-step-desc">Your no-sweat bet won! No free bet needed. Remove this promo when ready.</p>
            </div>
          )}
          {promo.step === 'free_bet' && (results.length > 0 || bestBets.length > 0) && (
            <div className="bet-and-get-step">
              <div className="bag-step-label">Step 2 of 2 — Convert Your ${promo.amount} Free Bet</div>
              <p className="bag-step-desc">
                Your initial bet lost, but {bookName} credited you a ${promo.amount} free bet. Pick an option below to convert it to guaranteed cash.
              </p>
            </div>
          )}
        </>
      )}

      {/* Split results layout — skip deposit_bonus (handled above), bet_and_get qualifying/awaiting, and no_sweat awaiting/done */}
      {!collapsed && ((promo.type !== 'bet_and_get' && promo.type !== 'deposit_bonus') || promo.step === 'bonus') && !(promo.type === 'no_sweat' && (promo.step === 'awaiting_result' || promo.step === 'done')) && (results.length > 0 || bestBets.length > 0) && (
        <div className={`promo-results-split${showBestBets ? '' : ' promo-results-single'} mobile-tab-${mobileTab}`}>
          <div className="promo-mobile-tabs">
            <button className={`pmt-btn${mobileTab === 'lock' ? ' active' : ''}`} onClick={() => setMobileTab('lock')}>🔒 Lock It In</button>
            <button className={`pmt-btn${mobileTab === 'best' ? ' active' : ''}`} onClick={() => setMobileTab('best')}>📈 Best Bet</button>
          </div>

          {/* Lock It In panel */}
          <div className="promo-panel promo-panel--lock">
            <div className="promo-panel-header">
              <span className="promo-panel-title">🔒 Lock It In</span>
              {bestGuaranteed !== null && (
                <span className="promo-panel-stat">+${bestGuaranteed.toFixed(2)} best</span>
              )}
            </div>
            {results.length > 0 && (
              <div className="promo-live-filter">
                {['all', 'live', 'upcoming'].map(opt => {
                  const locked = promo.liveStatus === 'live' && opt === 'upcoming';
                  return (
                    <button
                      key={opt}
                      className={`plf-btn${liveFilter === opt ? ' active' : ''}${locked ? ' locked' : ''}`}
                      onClick={() => { if (!locked) { setLiveFilter(opt); setExpandedLeft(false); } }}
                      disabled={locked}
                      title={locked ? 'This promo is for live games only' : undefined}
                    >
                      {opt === 'all' ? 'All' : opt === 'live' ? 'Live' : 'Upcoming'}
                    </button>
                  );
                })}
              </div>
            )}
            {(promo.type === 'free_bet' || promo.type === 'no_sweat' || promo.type === 'profit_boost' || promo.type === 'bet_and_get') && results.length > 0 && (
              <div className="hedge-budget-row">
                <label className="hedge-budget-label">Max hedge $</label>
                <input
                  type="number"
                  className="hedge-budget-input"
                  placeholder="any"
                  min={0}
                  value={maxHedge}
                  onChange={e => { setMaxHedge(e.target.value); setExpandedLeft(false); }}
                />
                {maxHedgeNum > 0 && displayResults.length < results.length && (
                  <span className="hedge-budget-note">{displayResults.length} of {results.length}</span>
                )}
              </div>
            )}
            {displayResults.length > 0 ? (
              <>
                <div className="promo-results">
                  {(expandedLeft ? displayResults.slice(0, BEST_BET_MAX) : displayResults.slice(0, PREVIEW_COUNT)).map((opp, i) => (
                    <PromoCard key={`${opp.event_id}-${opp.promo_side}-${i}`} opportunity={opp} session={session} qualifyingBetId={qualifyingBetId}
                      {...(promo.type === 'no_sweat' && promo.step === 'qualifying' ? { noteStage: 'initial', promoId: promo.id, onTracked: () => advanceStep() } : {})}
                    />
                  ))}
                </div>
                {displayResults.length > PREVIEW_COUNT && (
                  <button className="promo-expand-btn" onClick={() => setExpandedLeft(!expandedLeft)}>
                    {expandedLeft
                      ? <><ChevronUp size={14} /> Show less</>
                      : <><ChevronDown size={14} /> View all {Math.min(displayResults.length, BEST_BET_MAX)} options</>}
                  </button>
                )}
              </>
            ) : (
              <div className="promo-panel-empty">
                {maxHedgeNum > 0 && liveFilter !== 'all' && results.length > 0
                  ? `No ${liveFilter} opportunities within $${maxHedgeNum} hedge budget.`
                  : maxHedgeNum > 0 && results.length > 0
                  ? `No opportunities within $${maxHedgeNum} hedge budget.`
                  : liveFilter !== 'all' && results.length > 0
                  ? `No ${liveFilter} opportunities right now.`
                  : promo.type === 'profit_boost'
                  ? 'No automatic profits right now.'
                  : 'No hedging opportunities found.'}
              </div>
            )}
          </div>

          {/* Best Bet panel — hidden for deposit_bonus */}
          {showBestBets && (
            <div className="promo-panel promo-panel-ev promo-panel--best">
              {promo.type === 'profit_boost' && (
                <div className="promo-ev-tip">
                  💡 Profit boosts don't have to be hedged. If you're comfortable with some risk, you can skip the hedge and bet the boosted side outright — the inflated odds give you a built-in +EV edge.
                </div>
              )}
              <div className="promo-panel-header">
                <span className="promo-panel-title">📈 Best Bet</span>
                {bestEdge !== null && (
                  <span className="promo-panel-stat promo-panel-stat-ev">
                    {bestEdge > 0 ? '+' : ''}{bestEdge.toFixed(1)}% best edge
                  </span>
                )}
              </div>
              <div className="promo-live-filter">
                {['all', 'live', 'upcoming'].map(opt => {
                  const locked = promo.liveStatus === 'live' && opt === 'upcoming';
                  return (
                    <button key={opt}
                      className={`plf-btn${liveFilter === opt ? ' active' : ''}${locked ? ' locked' : ''}`}
                      onClick={() => { if (!locked) setLiveFilter(opt); }}
                      disabled={locked}>
                      {opt === 'all' ? 'All' : opt === 'live' ? 'Live' : 'Upcoming'}
                    </button>
                  );
                })}
              </div>

              {/* Max odds slider */}
              <div className="bbc-odds-slider">
                <div className="bbc-slider-label">
                  <span>Max odds</span>
                  <span className="bbc-slider-value">{oddsLabel(oddsScale[oddsIdx])}</span>
                </div>
                <input
                  type="range"
                  className="bbc-slider"
                  min={0}
                  max={oddsScale.length - 1}
                  step={1}
                  value={oddsIdx}
                  onChange={e => {
                    setOddsIdx(parseInt(e.target.value));
                    setExpandedRight(false);
                  }}
                />
                <div className="bbc-slider-ticks">
                  <span>{oddsLabel(oddsScale[0])}</span>
                  <span>{oddsLabel(oddsScale[Math.floor((oddsScale.length - 1) / 2)])}</span>
                  <span>{oddsLabel(oddsScale[oddsScale.length - 1])}</span>
                </div>
              </div>

              {displayBets.length > 0 ? (
                <>
                  <div className="promo-results">
                    {(expandedRight ? displayBets.slice(0, BEST_BET_MAX) : displayBets.slice(0, PREVIEW_COUNT)).map((bb, i) => (
                      <BestBetCard key={`${bb.event_id}-${bb.promo_side}-${i}`} opportunity={bb} promo={promo} bankroll={bankroll} kellyFraction={kellyFraction} session={session} qualifyingBetId={qualifyingBetId}
                        {...(promo.type === 'no_sweat' && promo.step === 'qualifying' ? { noteStage: 'initial', promoId: promo.id, onTracked: () => advanceStep() } : {})}
                      />
                    ))}
                  </div>
                  {displayBets.length > PREVIEW_COUNT && (
                    <button className="promo-expand-btn" onClick={() => setExpandedRight(!expandedRight)}>
                      {expandedRight
                        ? <><ChevronUp size={14} /> Show less</>
                        : <><ChevronDown size={14} /> View all {Math.min(displayBets.length, BEST_BET_MAX)} options</>}
                    </button>
                  )}
                </>
              ) : (
                <div className="promo-panel-empty">No best-bet plays found.</div>
              )}
            </div>
          )}

        </div>
      )}
      {promo.type === 'no_sweat' && promo.step === 'qualifying' && !session && (results.length > 0 || bestBets.length > 0) && (
        <button className="bag-advance-btn" onClick={() => advanceStep()}>
          I've placed my initial bet →
        </button>
      )}
    </div>
  );
}

const CROSS_HEDGE_ELIGIBLE = new Set(['free_bet', 'no_sweat', 'profit_boost']);

function PromoTool({ selectedBooks, bankroll, kellyFraction, session }) {
  const [promos, setPromos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [hedgePairs, setHedgePairs] = useState([]);
  const [hedgePairsLoading, setHedgePairsLoading] = useState(false);
  const [hedgePairsError, setHedgePairsError] = useState(null);
  const [hedgePairsExpanded, setHedgePairsExpanded] = useState(false);
  const [hedgePairsLiveFilter, setHedgePairsLiveFilter] = useState('all');
  const anyLiveOnly = promos.some(p => CROSS_HEDGE_ELIGIBLE.has(p.type) && p.liveStatus === 'live');

  useEffect(() => {
    if (anyLiveOnly) setHedgePairsLiveFilter(f => f === 'upcoming' ? 'live' : f);
  }, [anyLiveOnly]);

  useEffect(() => {
    if (!session) { setPromos(loadPromos()); return; }
    fetch(`${API_BASE}/user/promos`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setPromos(d.promos); else setPromos(loadPromos()); })
      .catch(() => setPromos(loadPromos()));
  }, [session]);

  async function handleAddPromo(promo) {
    if (!session) {
      const updated = [...promos, promo];
      setPromos(updated);
      savePromos(updated);
      return;
    }
    const r = await fetch(`${API_BASE}/user/promos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(promo),
    });
    const d = await r.json();
    if (d.success) setPromos(prev => [d.promo, ...prev]);
  }

  async function handleRemovePromo(id) {
    localStorage.removeItem(`bag_qid_${id}`);
    localStorage.removeItem(`deposit_rolled_${id}`);
    if (!session) {
      const updated = promos.filter(p => p.id !== id);
      setPromos(updated);
      savePromos(updated);
      return;
    }
    await fetch(`${API_BASE}/user/promos/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    setPromos(prev => prev.filter(p => p.id !== id));
  }

  async function handleUpdatePromo(updatedPromo) {
    if (!session) {
      const updated = promos.map(p => p.id === updatedPromo.id ? updatedPromo : p);
      setPromos(updated);
      savePromos(updated);
      return;
    }
    const r = await fetch(`${API_BASE}/user/promos/${updatedPromo.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(updatedPromo),
    });
    const d = await r.json();
    if (d.success) setPromos(prev => prev.map(p => p.id === d.promo.id ? d.promo : p));
  }

  function handleEditPromo(promo) {
    setEditingPromo(promo);
  }

  async function handleSavePromo(updatedPromo) {
    if (!session) {
      const updated = promos.map(p => p.id === updatedPromo.id ? updatedPromo : p);
      setPromos(updated);
      savePromos(updated);
      setEditingPromo(null);
      return;
    }
    const r = await fetch(`${API_BASE}/user/promos/${updatedPromo.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(updatedPromo),
    });
    const d = await r.json();
    if (d.success) setPromos(prev => prev.map(p => p.id === d.promo.id ? d.promo : p));
    setEditingPromo(null);
  }

  const fetchHedgePairs = useCallback(async () => {
    const eligible = promos.filter(p => CROSS_HEDGE_ELIGIBLE.has(p.type));
    if (eligible.length < 2) {
      setHedgePairs([]);
      setHedgePairsError(null);
      return;
    }
    setHedgePairsLoading(true);
    setHedgePairsError(null);
    try {
      const res = await fetch(`${API_BASE}/promos/hedge-pairs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promos: eligible }),
      });
      const data = await res.json();
      if (data.success) {
        setHedgePairs(data.hedge_pairs || []);
      } else {
        setHedgePairsError(data.error || 'Failed to fetch cross-promo opportunities');
      }
    } catch {
      setHedgePairsError('Network error — is the backend running?');
    } finally {
      setHedgePairsLoading(false);
    }
  }, [promos]);

  useEffect(() => {
    fetchHedgePairs();
    const interval = setInterval(fetchHedgePairs, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchHedgePairs]);

  const eligibleCount = promos.filter(p => CROSS_HEDGE_ELIGIBLE.has(p.type)).length;

  const now = Date.now();
  const displayHedgePairs = hedgePairs.filter(pair => {
    const promoA = promos.find(p => p.id === pair.promo_a_id);
    const promoB = promos.find(p => p.id === pair.promo_b_id);
    const pairLiveOnly = promoA?.liveStatus === 'live' || promoB?.liveStatus === 'live';
    const effectiveFilter = pairLiveOnly ? 'live' : hedgePairsLiveFilter;
    if (effectiveFilter === 'live') return pair.commence_time && new Date(pair.commence_time).getTime() <= now;
    if (effectiveFilter === 'upcoming') return !pair.commence_time || new Date(pair.commence_time).getTime() > now;
    return true;
  });

  return (
    <div className="promo-tool">
      <div className="promo-tool-header">
        <div>
          <h1>Promo Tool</h1>
          <p className="promo-tool-note">For single-game bets only — not intended for parlays.</p>
        </div>
        <button className="add-promo-btn" onClick={() => setShowModal(true)}>
          <Plus size={18} />
          Add Promo
        </button>
      </div>

      {promos.length === 0 ? (
        <div className="promo-empty-state">
          <div className="promo-empty-icon">🎁</div>
          <h3>No promotions added yet</h3>
          <p>Add a free bet, profit boost, or deposit bonus to see your best options.</p>
        </div>
      ) : (
        <>
          {eligibleCount >= 2 && (
            <div className="cross-promo-section">
              <div className="cross-promo-header">
                <span className="cross-promo-title">Cross-Promo Opportunities</span>
                <span className="cross-promo-subtitle">Use two promos on opposite sides — no (or minimal) real money needed</span>
              </div>

              <div className="promo-live-filter">
                {['all', 'live', 'upcoming'].map(opt => {
                  const locked = anyLiveOnly && opt === 'upcoming';
                  return (
                    <button
                      key={opt}
                      className={`plf-btn${hedgePairsLiveFilter === opt ? ' active' : ''}${locked ? ' locked' : ''}`}
                      onClick={() => { if (!locked) { setHedgePairsLiveFilter(opt); setHedgePairsExpanded(false); } }}
                      disabled={locked}
                      title={locked ? 'One or more promos are for live games only' : undefined}
                    >
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </button>
                  );
                })}
              </div>

              {hedgePairsLoading && (
                <div className="promo-loading">
                  <Loader size={18} className="spin" />
                  <span>Scanning for cross-promo matches…</span>
                </div>
              )}

              {hedgePairsError && !hedgePairsLoading && (
                <div className="promo-error">{hedgePairsError}</div>
              )}

              {!hedgePairsLoading && !hedgePairsError && displayHedgePairs.length === 0 && (
                <div className="promo-panel-empty">
                  {hedgePairs.length > 0
                    ? `No ${hedgePairsLiveFilter} cross-promo opportunities right now.`
                    : 'No matching events found for your current promos. Try adding promos at different books or for different sports.'}
                </div>
              )}

              {displayHedgePairs.length > 0 && (
                <>
                  <div className="cross-promo-cards">
                    {(hedgePairsExpanded ? displayHedgePairs.slice(0, 10) : displayHedgePairs.slice(0, 3)).map((pair, i) => (
                      <HedgePairCard
                        key={`${pair.event_id}-${pair.promo_a_id}-${pair.promo_b_id}-${i}`}
                        pair={pair}
                        session={session}
                      />
                    ))}
                  </div>
                  {displayHedgePairs.length > 3 && (
                    <button
                      className="promo-expand-btn"
                      onClick={() => setHedgePairsExpanded(e => !e)}
                    >
                      {hedgePairsExpanded
                        ? <><ChevronUp size={14} /> Show less</>
                        : <><ChevronDown size={14} /> View all {Math.min(displayHedgePairs.length, 10)} cross-promo options</>}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          <div className="promo-sections">
            {promos.map(promo => (
              <PromoSection
                key={promo.id}
                promo={promo}
                selectedBooks={selectedBooks}
                onRemove={handleRemovePromo}
                onUpdate={handleUpdatePromo}
                onEdit={handleEditPromo}
                bankroll={bankroll}
                kellyFraction={kellyFraction}
                session={session}
              />
            ))}
          </div>
        </>
      )}

      {showModal && (
        <AddPromoModal
          onAdd={handleAddPromo}
          onClose={() => setShowModal(false)}
          selectedBooks={selectedBooks}
        />
      )}

      {editingPromo && (
        <AddPromoModal
          editPromo={editingPromo}
          onSave={handleSavePromo}
          onClose={() => setEditingPromo(null)}
          selectedBooks={selectedBooks}
        />
      )}
    </div>
  );
}

export default PromoTool;
